"use client"

import { type ChangeEvent, useTransition } from "react"
import {
  DEV_ROLE_OVERRIDE_OPTIONS,
  type DevRoleOverride,
} from "@/lib/auth/devRoleOverride"
import { setDevRoleOverride } from "@/lib/auth/setDevRoleOverride"

type Props = {
  current: DevRoleOverride | null
}

// Dev-only banner control. Cycles the user's perceived role for spot-checking
// rep/manager/admin views without re-logging in. Selecting "Actual" clears
// the cookie and falls back to the real JWT roles. Only swaps what the FE
// sees — the backend still enforces against the real session.
const TOOLTIP =
  "Frontend-only override for spot-checking permission UI. " +
  "Backend API calls still run as your real signed-in account — this " +
  "never grants or revokes server-side access."

export const DevRoleToggle = ({ current }: Props) => {
  const [isPending, startTransition] = useTransition()
  const isOverridden = current !== null

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value
    startTransition(async () => {
      await setDevRoleOverride(value === "" ? null : value)
    })
  }

  return (
    <label
      title={TOOLTIP}
      className="flex items-center gap-1.5 rounded bg-black/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest"
    >
      <span aria-hidden="true">Role (FE only):</span>
      <span className="sr-only">{TOOLTIP}</span>
      <select
        value={current ?? ""}
        onChange={handleChange}
        disabled={isPending}
        className="bg-transparent text-current font-semibold uppercase tracking-widest outline-none disabled:opacity-50"
      >
        <option value="">No override</option>
        {DEV_ROLE_OVERRIDE_OPTIONS.map((role) => (
          <option key={role} value={role}>
            {role}
          </option>
        ))}
      </select>
      {isOverridden && (
        <span
          aria-hidden="true"
          className="rounded bg-black/25 px-1 py-px text-[9px] normal-case tracking-normal"
        >
          backend unchanged
        </span>
      )}
    </label>
  )
}
